import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  forwardRef,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-chip-search-input',
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule],
  templateUrl: './chip-search-input.component.html',
  styleUrls: ['./chip-search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipSearchInputComponent),
      multi: true
    }
  ]
})
export class ChipSearchInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  // Configuration
  @Input() placeholder: string = 'Search...';
  @Input() icon: string = 'search-outline';
  @Input() label: string = '';
  @Input() labelIcon: string = '';
  @Input() suggestions: string[] = [];
  @Input() disabled: boolean = false;
  @Input() minChars: number = 2;
  @Input() maxChips: number = 0; // 0 = unlimited
  @Input() set value(val: string[]) {
    if (val && Array.isArray(val)) {
      this.chips = [...val];
      this.cdr.markForCheck();
    }
  }

  // Events
  @Output() searchChange = new EventEmitter<string>();
  @Output() chipsChange = new EventEmitter<string[]>();

  // Internal state
  chips: string[] = [];
  searchTerm: string = '';
  showSuggestions: boolean = false;
  focusedSuggestionIndex: number = -1;
  isFocused: boolean = false;

  private searchDebounceTimer: any;
  private readonly DEBOUNCE_DELAY = 300;

  // ControlValueAccessor
  private onChange = (value: string[]) => {};
  private onTouched = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {}

  ngOnDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  // ControlValueAccessor
  writeValue(value: string[]): void {
    this.chips = value || [];
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // Click outside to close suggestions
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeSuggestions();
    }
  }

  // Input handling
  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.focusedSuggestionIndex = -1;

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    if (this.searchTerm.length >= this.minChars) {
      this.searchDebounceTimer = setTimeout(() => {
        this.searchChange.emit(this.searchTerm);
        this.showSuggestions = true;
        this.cdr.markForCheck();
      }, this.DEBOUNCE_DELAY);
    } else {
      this.showSuggestions = false;
      this.cdr.markForCheck();
    }
  }

  onInputFocus() {
    this.isFocused = true;
    this.onTouched();
    if (this.searchTerm.length >= this.minChars && this.filteredSuggestions.length > 0) {
      this.showSuggestions = true;
    }
    this.cdr.markForCheck();
  }

  onInputBlur() {
    // Delay to allow click on suggestion
    setTimeout(() => {
      this.isFocused = false;
      this.cdr.markForCheck();
    }, 200);
  }

  // Keyboard navigation
  onKeyDown(event: KeyboardEvent) {
    const filtered = this.filteredSuggestions;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.showSuggestions && filtered.length > 0) {
          this.focusedSuggestionIndex = Math.min(
            this.focusedSuggestionIndex + 1,
            filtered.length - 1
          );
          this.cdr.markForCheck();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.showSuggestions) {
          this.focusedSuggestionIndex = Math.max(
            this.focusedSuggestionIndex - 1,
            -1
          );
          this.cdr.markForCheck();
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (this.focusedSuggestionIndex >= 0 && this.focusedSuggestionIndex < filtered.length) {
          this.selectSuggestion(filtered[this.focusedSuggestionIndex]);
        } else if (this.searchTerm.trim().length >= this.minChars) {
          // Allow adding custom chip on Enter
          this.addChip(this.searchTerm.trim());
        }
        break;

      case 'Backspace':
        if (this.searchTerm === '' && this.chips.length > 0) {
          this.removeChip(this.chips.length - 1);
        }
        break;

      case 'Escape':
        this.closeSuggestions();
        break;
    }
  }

  // Chip management
  selectSuggestion(suggestion: string) {
    this.addChip(suggestion);
    this.closeSuggestions();
  }

  addChip(value: string) {
    const normalizedValue = value.trim();
    if (!normalizedValue) return;

    // Check for duplicates (case-insensitive)
    const isDuplicate = this.chips.some(
      chip => chip.toLowerCase() === normalizedValue.toLowerCase()
    );
    if (isDuplicate) return;

    // Check max chips
    if (this.maxChips > 0 && this.chips.length >= this.maxChips) return;

    this.chips = [...this.chips, normalizedValue];
    this.searchTerm = '';
    this.showSuggestions = false;
    this.focusedSuggestionIndex = -1;

    this.emitChanges();
    this.focusInput();
  }

  removeChip(index: number) {
    this.chips = this.chips.filter((_, i) => i !== index);
    this.emitChanges();
    this.focusInput();
  }

  clearAll() {
    this.chips = [];
    this.searchTerm = '';
    this.showSuggestions = false;
    this.emitChanges();
    this.focusInput();
  }

  // Filtered suggestions (exclude already selected chips)
  get filteredSuggestions(): string[] {
    if (!this.suggestions || this.suggestions.length === 0) return [];
    const chipsLower = this.chips.map(c => c.toLowerCase());
    return this.suggestions.filter(
      s => !chipsLower.includes(s.toLowerCase())
    );
  }

  // Public method to reset the component from parent
  reset() {
    this.chips = [];
    this.searchTerm = '';
    this.showSuggestions = false;
    this.focusedSuggestionIndex = -1;
    this.emitChanges();
    this.cdr.markForCheck();
  }

  private emitChanges() {
    this.onChange(this.chips);
    this.chipsChange.emit(this.chips);
    this.cdr.markForCheck();
  }

  private closeSuggestions() {
    this.showSuggestions = false;
    this.focusedSuggestionIndex = -1;
    this.cdr.markForCheck();
  }

  private focusInput() {
    setTimeout(() => {
      if (this.searchInput?.nativeElement) {
        this.searchInput.nativeElement.focus();
      }
    }, 0);
  }

  trackByChip(index: number, chip: string): string {
    return chip;
  }

  trackBySuggestion(index: number, suggestion: string): string {
    return suggestion;
  }
}
